import { useRef, useState } from 'react'
import HeaderBar from './HeaderBar'
import { useValuationPlausibility } from './ValuationPlausibility.data'
import { Box, Stack, styled } from '@mui/material'
import { Footer, tokens } from 'ComponentLibrary'
import { IconButton } from 'ComponentLibrary/src/piazza'
import { CaretDoubleLeftIcon, CaretDoubleRightIcon, FilesIcon, HighlighterIcon, TableIcon } from '@phosphor-icons/react'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import PlausibilityDeviationAnalysis from './PlausibilityDeviationAnalysis'
import PlausibilityAnnotions from './PlausibilityAnnotations'
import PlausibilityPdf from './PlausibilityPdf'
import { maxBy, partition } from 'lodash'
import {
    Annotation,
    isDeviationAnalysisAnnotation,
    isPdfAnnotation,
    isPlausibilityVisibleToAppraisers,
} from '@einwert/valuation-api-client'
import dayjs from 'dayjs'
import { HandoverModal } from './HandoverModal'
import { ApprovalModal } from './ApprovalModal'
import { HandoverApprovalSection } from './HandoverApprovalSection'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { InternalApprovalModal } from './InternalApprovalModal'
import { useTranslation } from 'react-i18next'
import type { UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'
import { getTextBubbleCopy } from './textBubbleCopy'

export default function ValuationPlausibility() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tValuationPlausibility } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.valuationPlausibility',
    })
    const { t: tTextBubble } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.valuationPlausibility.textBubble',
    })
    const userLinkCopy: Partial<UserLinkCopy> = {
        me: tValuationPlausibility('userLink.me'),
        external: tValuationPlausibility('userLink.external'),
        unknown: tValuationPlausibility('userLink.unknown'),
        contact: tValuationPlausibility('userLink.contact'),
        editOwnContactTooltip: tValuationPlausibility('userLink.editOwnContactTooltip'),
        emailCopied: tValuationPlausibility('userLink.emailCopied'),
        phoneCopied: tValuationPlausibility('userLink.phoneCopied'),
        addressCopied: tValuationPlausibility('userLink.addressCopied'),
        note: tValuationPlausibility('userLink.note'),
        externalEmailInfo: tValuationPlausibility('userLink.externalEmailInfo'),
    }
    const textBubbleCopy = getTextBubbleCopy(tTextBubble)
    const { isPlausibilityInternalApprovalEnabled } = useFlags()
    const {
        asset,
        valuationId,
        valuation,
        annotations,
        draftOptions,
        participantIds,
        observerUUIDs,
        setObserverUUIDs,
        addObserver,
        removeObserver,
        setIsHandOver,
        setIsApproved,
        setIsInternallyApproved,
        currentUser,
        users,
        observerUsers,
        participantUsers,
        appraiserUsers,
        isAppraiserUsersPending,
        isEditObserversPending,
        isEditPlausibilityPending,
        orderAssignedCustomerUsers,
        orderAssignedAppraiserId,
        plausibility,
        isValuationPlausibilityPending,
    } = useValuationPlausibility()
    const [onlyShowCurrentDraftAnnotations, setOnlyShowCurrentDraftAnnotations] = useState(false)
    const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true)
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(true)
    const [isEditingObservers, setIsEditingObservers] = useState(false)
    const [isHandoverOpen, setIsHandoverOpen] = useState(false)
    const [isApprovalOpen, setIsApprovalOpen] = useState(false)
    const [isInternalApprovalOpen, setIsInternalApprovalOpen] = useState(false)
    const [selectedAppraiserId, setSelectedAppraiserId] = useState<number | undefined>(undefined)

    const [activePage, setActivePage] = useState(1)
    const [jumpPageInput, setJumpPageInput] = useState<string>('1')
    const [numberOfPages, setNumberOfPages] = useState(0)

    const pageRefs = useRef<(HTMLDivElement | null)[]>([])

    const goToPage = (page: number) => {
        const newPage = page < 1 ? 1 : page > numberOfPages ? numberOfPages : page

        setActivePage(newPage)
        setJumpPageInput(String(newPage))
        pageRefs.current[newPage - 1]?.scrollIntoView({ block: 'start' })
    }

    const hasNewerDraft =
        maxBy(draftOptions, (draftOption) => dayjs(draftOption.created_on).valueOf())?.uuid !== plausibility?.parent_draft_uuid

    const annotationsAdjustedForDraft: Annotation[] = annotations.map((annotation) => ({
        ...annotation,
        is_read_only: hasNewerDraft ? true : annotation.is_read_only,
        replies: annotation.replies
            ?.filter((reply) => !reply.is_deleted)
            .map((reply) => ({ ...reply, is_read_only: hasNewerDraft ? true : reply.is_read_only })),
    }))

    const filteredAnnotations = onlyShowCurrentDraftAnnotations
        ? annotationsAdjustedForDraft.filter(
              (annotation) =>
                  annotation.original_draft_uuid === valuation?.uuid ||
                  annotation.replies?.some((reply) => reply.original_draft_uuid === valuation?.uuid)
          )
        : annotationsAdjustedForDraft

    const [pdfAnnotations, remainingAfterPdf] = partition(filteredAnnotations, isPdfAnnotation)
    const [deviationAnnotations, generalAnnotations] = partition(remainingAfterPdf, isDeviationAnalysisAnnotation)
    const unfilteredDeviationAnnotations = annotationsAdjustedForDraft.filter(isDeviationAnalysisAnnotation)

    const hasMultipleDraftOptions = draftOptions.length > 1
    const hasNewAnnotations = annotations.some(
        (annotation) => !annotation.is_read_only || annotation.replies?.some((reply) => !reply.is_read_only)
    )
    const hasAppraiserInObservers = observerUsers.some((user) => user.role === 'appraiser')
    const hasAppraiserInObserversOrParticipants = hasAppraiserInObservers || participantUsers.some((user) => user.role === 'appraiser')
    const isVisibleToAppraisers = isPlausibilityVisibleToAppraisers(plausibility)

    const hasMultipleDrafts = draftOptions.length > 1

    const closeHandoverModal = () => {
        setIsHandoverOpen(false)
        setSelectedAppraiserId(undefined)
    }

    const closeApprovalModal = () => {
        setIsApprovalOpen(false)
        setSelectedAppraiserId(undefined)
    }

    const closeInternalApprovalModal = () => {
        setIsInternalApprovalOpen(false)
        setSelectedAppraiserId(undefined)
    }

    const addSelectedAppraiserAsObserverIfNeeded = () => {
        if (hasAppraiserInObserversOrParticipants || selectedAppraiserId === undefined) {
            return
        }

        const selectedAppraiser = appraiserUsers.find((appraiser) => appraiser.id === selectedAppraiserId)
        if (!selectedAppraiser || observerUUIDs.includes(selectedAppraiser.uuid)) {
            return
        }

        addObserver(selectedAppraiser.uuid)
    }

    const getUpdatedParticipantIds = () => {
        if (hasAppraiserInObserversOrParticipants || selectedAppraiserId === undefined || participantIds.includes(selectedAppraiserId)) {
            return undefined
        }

        return [...participantIds, selectedAppraiserId]
    }

    const handleHandoverConfirm = () => {
        addSelectedAppraiserAsObserverIfNeeded()
        setIsHandOver(true)
        closeHandoverModal()
    }

    const handleApprovalConfirm = () => {
        addSelectedAppraiserAsObserverIfNeeded()
        setIsApproved(true)
        closeApprovalModal()
    }

    const handleInternalApprovalConfirm = () => {
        addSelectedAppraiserAsObserverIfNeeded()
        setIsInternallyApproved(true, getUpdatedParticipantIds())
        closeInternalApprovalModal()
    }

    return (
        <Stack direction="column" height="100%" width="100%">
            <HeaderBar
                asset={asset}
                draft={valuation}
                draftOptions={draftOptions}
                isApproved={plausibility?.is_approved}
                isVisibleToAppraisers={isVisibleToAppraisers}
                observerUUIDs={observerUUIDs}
                setObserverUUIDs={setObserverUUIDs}
                addObserver={addObserver}
                removeObserver={removeObserver}
                currentUser={currentUser}
                users={users}
                isEditingObservers={isEditingObservers}
                setIsEditingObservers={setIsEditingObservers}
                isEditObserversPending={isEditObserversPending}
                orderAssignedCustomerUsers={orderAssignedCustomerUsers}
                orderAssignedAppraiserId={orderAssignedAppraiserId}
                hasMultipleDrafts={hasMultipleDrafts}
                isValuationPlausibilityPending={isValuationPlausibilityPending}
                hasNewerDraft={hasNewerDraft}
            />

            <Stack direction="row" flex={1} minHeight={0}>
                <Stack direction="column" flex={isLeftPanelOpen ? 1 : 'none'} minWidth={0}>
                    <PanelHeaderContainer direction="row">
                        {isLeftPanelOpen ? (
                            <>
                                <TableIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
                                <Headline
                                    variant="headline3"
                                    title={tCommon('deviationAnalysis')}
                                    fullWidth
                                    iconButtons={[
                                        {
                                            icon: <CaretDoubleLeftIcon />,
                                            onClick: () => setIsLeftPanelOpen(false),
                                            tooltip: tCommonActions('collapse'),
                                        },
                                    ]}
                                />
                            </>
                        ) : (
                            <IconButton
                                size="small"
                                variant="tertiary"
                                icon={<CaretDoubleRightIcon />}
                                onClick={() => setIsLeftPanelOpen(true)}
                                tooltip={tCommonActions('expand')}
                            />
                        )}
                    </PanelHeaderContainer>
                    {isLeftPanelOpen && (
                        <PlausibilityDeviationAnalysis
                            selectedValuationId={valuationId}
                            deviationAnnotations={unfilteredDeviationAnnotations}
                            isImmediatelyVisible={isVisibleToAppraisers}
                            hasMultipleDrafts={hasMultipleDrafts}
                            hasNewerDraft={hasNewerDraft}
                            filterOlderAnnotations={onlyShowCurrentDraftAnnotations}
                            textBubbleCopy={textBubbleCopy}
                            userLinkCopy={userLinkCopy}
                        />
                    )}
                </Stack>
                <Stack
                    direction="column"
                    borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
                    minWidth="621px"
                    flex={1}
                    boxShadow={`inset 0 -${tokens.SizingBorderM} 0 0 ${tokens.ColorBorderDefault}`}
                >
                    <PanelHeaderContainer direction="row">
                        <FilesIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
                        <Headline variant="headline3" title={tValuationPlausibility('panels.pdf.title')} fullWidth />
                    </PanelHeaderContainer>
                    <PlausibilityPdf
                        valuation={valuation}
                        PdfAnnotations={pdfAnnotations}
                        isImmediatelyVisible={isVisibleToAppraisers}
                        hasMultipleDrafts={hasMultipleDrafts}
                        activePage={activePage}
                        setActivePage={setActivePage}
                        pageRefs={pageRefs}
                        numberOfPages={numberOfPages}
                        setNumberOfPages={setNumberOfPages}
                        jumpPageInput={jumpPageInput}
                        setJumpPageInput={setJumpPageInput}
                        goToPage={goToPage}
                        hasNewerDraft={hasNewerDraft}
                        textBubbleCopy={textBubbleCopy}
                        userLinkCopy={userLinkCopy}
                    />
                </Stack>
                <Stack
                    direction="column"
                    borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
                    width={isRightPanelOpen ? '392px' : 'auto'}
                    minWidth={isRightPanelOpen ? '392px' : 'auto'}
                    maxWidth={isRightPanelOpen ? '392px' : 'auto'}
                    flexShrink={0}
                    minHeight={0}
                >
                    <PanelHeaderContainer direction="row">
                        {isRightPanelOpen ? (
                            <>
                                <HighlighterIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
                                <Headline
                                    variant="headline3"
                                    title={tValuationPlausibility('panels.annotations.title')}
                                    fullWidth
                                    iconButtons={[
                                        {
                                            icon: <CaretDoubleRightIcon />,
                                            onClick: () => setIsRightPanelOpen(false),
                                            tooltip: tCommonActions('collapse'),
                                        },
                                    ]}
                                />
                            </>
                        ) : (
                            <IconButton
                                size="small"
                                variant="tertiary"
                                icon={<CaretDoubleLeftIcon />}
                                onClick={() => setIsRightPanelOpen(true)}
                                tooltip={tCommonActions('expand')}
                            />
                        )}
                    </PanelHeaderContainer>
                    {isRightPanelOpen && (
                        <PlausibilityAnnotions
                            pdfAnnotations={pdfAnnotations}
                            generalAnnotations={generalAnnotations}
                            deviationAnnotations={deviationAnnotations}
                            isImmediatelyVisible={isVisibleToAppraisers}
                            hasMultipleDraftOptions={hasMultipleDraftOptions}
                            onlyShowCurrentDraftAnnotations={onlyShowCurrentDraftAnnotations}
                            setOnlyShowCurrentDraftAnnotations={setOnlyShowCurrentDraftAnnotations}
                            hasNewerDraft={hasNewerDraft}
                            hasMultipleDrafts={hasMultipleDrafts}
                            goToPage={goToPage}
                            isValuationPlausibilityPending={isValuationPlausibilityPending}
                            textBubbleCopy={textBubbleCopy}
                            userLinkCopy={userLinkCopy}
                        />
                    )}
                    {isRightPanelOpen && (
                        <HandoverApprovalSection
                            isHandedOver={plausibility?.is_handed_over}
                            handedOverOn={plausibility?.handed_over_on}
                            handedOverBy={plausibility?.handed_over_by}
                            isApproved={plausibility?.is_approved}
                            approvedOn={plausibility?.approved_on}
                            approvedBy={plausibility?.approved_by}
                            isInternallyApproved={plausibility?.is_internally_approved}
                            internallyApprovedOn={plausibility?.internally_approved_on}
                            internallyApprovedBy={plausibility?.internally_approved_by}
                            showInternalApproval={isPlausibilityInternalApprovalEnabled}
                            users={users}
                            hasAnnotationsToHandover={hasNewAnnotations}
                            hasNewerDraft={hasNewerDraft}
                            isValuationPlausibilityPending={isValuationPlausibilityPending}
                            onOpenHandoverModal={() => {
                                setSelectedAppraiserId(undefined)
                                setIsHandoverOpen(true)
                            }}
                            onOpenApprovalModal={() => {
                                setSelectedAppraiserId(undefined)
                                setIsApprovalOpen(true)
                            }}
                            onOpenInternalApprovalModal={() => {
                                setSelectedAppraiserId(undefined)
                                setIsInternalApprovalOpen(true)
                            }}
                        />
                    )}
                </Stack>
            </Stack>
            <HandoverModal
                isHandoverOpen={isHandoverOpen}
                onConfirm={handleHandoverConfirm}
                onClose={closeHandoverModal}
                isPersisting={isEditPlausibilityPending || isEditObserversPending}
                hasAppraiserInObservers={hasAppraiserInObserversOrParticipants}
                appraiserUsers={appraiserUsers}
                selectedAppraiserId={selectedAppraiserId}
                setSelectedAppraiserId={setSelectedAppraiserId}
                isAppraiserUsersPending={isAppraiserUsersPending}
            />
            <ApprovalModal
                isApprovalOpen={isApprovalOpen}
                onClose={closeApprovalModal}
                onConfirm={handleApprovalConfirm}
                hasNewAnnotations={hasNewAnnotations}
                commentsAreAlreadyVisible={isVisibleToAppraisers}
                isPersisting={isEditPlausibilityPending || isEditObserversPending}
                hasAppraiserInObservers={hasAppraiserInObserversOrParticipants}
                appraiserUsers={appraiserUsers}
                selectedAppraiserId={selectedAppraiserId}
                setSelectedAppraiserId={setSelectedAppraiserId}
                isAppraiserUsersPending={isAppraiserUsersPending}
            />
            <InternalApprovalModal
                isOpen={isInternalApprovalOpen}
                onClose={closeInternalApprovalModal}
                onConfirm={handleInternalApprovalConfirm}
                isPersisting={isEditPlausibilityPending || isEditObserversPending}
                commentsAreAlreadyVisible={isVisibleToAppraisers}
                hasAppraiserInObserversOrParticipants={hasAppraiserInObserversOrParticipants}
                appraiserUsers={appraiserUsers}
                selectedAppraiserId={selectedAppraiserId}
                setSelectedAppraiserId={setSelectedAppraiserId}
                isAppraiserUsersPending={isAppraiserUsersPending}
            />
            <Box width={'100%'} borderTop={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                <Footer />
            </Box>
        </Stack>
    )
}

const PanelHeaderContainer = styled(Stack)(() => ({
    padding: `${tokens.SizingSpacing150} ${tokens.SizingSpacing200}`,
    gap: tokens.SizingSpacing100,
    boxShadow: `inset 0 -${tokens.SizingBorderM} 0 0 ${tokens.ColorBorderDefault}`,
    alignItems: 'center',
}))
